import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'

import { FlexColumn, FlexRow } from '../styles/Containers'
import { ErrorText, SmallBodyText } from '../styles/Text'
import { MUTED_TEXT } from '../styles/colors'
import { IconButton, SecondaryIconButton } from '../common/Button'
import { useRenameFile } from '../files/renameFileRequest'
import FilenameInput from '../files/FilenameInput'

const Editor = styled(FlexColumn)`
  width: 100%;
  min-width: 0;
  cursor: default;
`

const EditorWrapper = styled.div`
  container-type: inline-size;
  width: 100%;
`

const EditorRow = styled(FlexRow)`
  gap: 6px;
  align-items: center;

  @container (max-width: 160px) {
    flex-wrap: wrap;
  }
`

const HelperText = styled(SmallBodyText)`
  margin-top: 5px;
  color: ${MUTED_TEXT};
`

function InlineRenameEditor({ file, onSaved, onCancel, onSavingChange = () => {} }) {
  const rename = useRenameFile(file)
  const helperId = `rename-helper-${file.id}`

  function cancel() {
    if (rename.isSaving) return
    onCancel()
  }

  async function save() {
    if (!rename.canSubmit) return
    onSavingChange(true)
    const updated = await rename.submit()
    onSavingChange(false)
    if (updated === null) {
      onCancel()
    } else if (updated) {
      onSaved(updated)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  return (
    <Editor onClick={e => e.stopPropagation()} data-testid="rename-editor" gap="0">
      <EditorWrapper>
        <EditorRow>
          <FilenameInput
            size="small"
            value={rename.stem}
            extension={rename.extension}
            onChange={rename.setStem}
            onKeyDown={handleKeyDown}
            error={!!rename.errorText}
            disabled={rename.isSaving}
            autoFocus
            ariaDescribedBy={helperId}
          />
          <IconButton aria-label="Save name" onClick={save} disabled={!rename.canSubmit} loading={rename.isSaving}>
            <CheckIcon />
          </IconButton>
          <SecondaryIconButton aria-label="Cancel rename" onClick={cancel} disabled={rename.isSaving}>
            <CloseIcon />
          </SecondaryIconButton>
        </EditorRow>
      </EditorWrapper>
      {rename.errorText ? (
        <ErrorText role="alert" id={helperId}>
          {rename.errorText}
        </ErrorText>
      ) : (
        <HelperText id={helperId}>Enter to save · Esc to cancel{rename.extension ? ` · Extension stays ${rename.extension}` : ''}</HelperText>
      )}
    </Editor>
  )
}

InlineRenameEditor.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSavingChange: PropTypes.func
}

export default InlineRenameEditor
